import { FC, useRef, useState } from 'react'
import { Box, MenuItem, Typography, styled } from '@mui/material'
import { Avatar, Dropdown, Tooltip } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import { imagesDisabled, getFullImageUrl } from 'utils'
import { useTranslation } from 'react-i18next'

export interface IPersonDetailsProps {
    size?: any
    person?: any
    enabled?: boolean
    loading?: boolean
    personMoreOptions?: any[]
}

const StyledMenuItem = styled(MenuItem)({
    height: '32px',
    minHeight: '32px',
})

export const PersonDetails: FC<IPersonDetailsProps> = (props) => {
    const { t } = useTranslation()
    const { size, person, enabled, personMoreOptions, loading } = props
    const [open, setOpen] = useState(false)
    const anchorRef = useRef(null)

    return (
        <RootContainer>
            {!imagesDisabled && (
                <AvatarContainer>
                    <Avatar
                        size={size}
                        src={getFullImageUrl(person.imageThumbUrl)}
                        style={{
                            boxShadow: 'rgb(110 110 111) 0px 0px 8px 0px',
                        }}
                    />
                </AvatarContainer>
            )}

            <Box width="100%">
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {person.friendlyName && (
                        <Typography
                            sx={{
                                fontSize: '1.4rem',
                                color: '#00000',
                                lineHeight: 1.14,
                                fontWeight: 'bold',
                            }}
                        >
                            {person.friendlyName}
                        </Typography>
                    )}

                    {personMoreOptions && personMoreOptions?.length > 0 && (
                        <div
                            style={{
                                height: '2rem',
                                cursor: 'pointer',
                                position: 'relative',
                            }}
                            onClick={() => {
                                setOpen((prev) => !prev)
                            }}
                        >
                            <Icon
                                name="Arrow"
                                color="black"
                                height="2rem"
                                width="2rem"
                            />
                            <span
                                ref={anchorRef}
                                style={{
                                    position: 'absolute',
                                    top: '4.1rem',
                                    left: '3.1rem',
                                }}
                            />
                            <Dropdown
                                anchorEl={anchorRef.current}
                                showCone={true}
                                open={open}
                                listStyles={{
                                    paddingBottom: '0',

                                    '& > ul': {
                                        paddingBottom: '0.7rem',
                                        paddingTop: '0',
                                        color: '#534e52',
                                        '& li': {
                                            borderRadius: '0.5rem',
                                            marginBottom: '0.8rem',
                                        },
                                        '& > li:last-child': {
                                            marginBottom: '0rem',
                                        },
                                        '& :hover': {
                                            color: '#1b1f1f',
                                        },
                                    },
                                }}
                                iconPosition={{
                                    top: '-0.5rem',
                                    right: '0',
                                }}
                                width="auto"
                                disableScrollLock={false}
                            >
                                {personMoreOptions?.map((o, i) => (
                                    <StyledMenuItem
                                        key={i + o.label}
                                        data-protectedsubcomponent={
                                            o?.requireAccess?.control
                                        }
                                        onClick={() => {
                                            o.onClick()
                                        }}
                                    >
                                        {t(o.label)}
                                    </StyledMenuItem>
                                ))}
                            </Dropdown>
                        </div>
                    )}
                </Box>

                {!imagesDisabled && (
                    <IconsContainer>
                        {person.email ? (
                            <a href={`mailto:${person.email}`}>
                                <Tooltip
                                    title={`mailto:${person.email}`}
                                    disableInteractive={false}
                                >
                                    <div>
                                        <Icon name="EmailRound" />
                                    </div>
                                </Tooltip>
                            </a>
                        ) : (
                            <Tooltip
                                title={`${
                                    loading
                                        ? t('Loading')
                                        : t('No_Email_Tooltip')
                                }`}
                                disableInteractive={false}
                            >
                                <div>
                                    {!enabled || loading ? (
                                        <Icon name="EmailRound" />
                                    ) : (
                                        <Icon
                                            name="EmailRound"
                                            style={{
                                                color: 'B4B4B4',
                                                cursor: 'default',
                                            }}
                                        />
                                    )}
                                </div>
                            </Tooltip>
                        )}
                        {person.telephone ? (
                            <a href={`tel:${person.telephone}`}>
                                <Tooltip
                                    title={person.telephone}
                                    style={{ marginLeft: '5px' }}
                                    disableInteractive={false}
                                >
                                    <div>
                                        <Icon name="PhoneRound" />
                                    </div>
                                </Tooltip>
                            </a>
                        ) : (
                            <Tooltip
                                title={`${
                                    loading
                                        ? t('Loading')
                                        : t('No_Phone_Tooltip')
                                }`}
                                style={{ marginLeft: '5px', cursor: 'pointer' }}
                                disableInteractive={false}
                            >
                                <div>
                                    {!enabled || loading ? (
                                        <Icon name="PhoneRound" />
                                    ) : (
                                        <Icon
                                            name="PhoneRound"
                                            style={{
                                                color: 'B4B4B4',
                                                cursor: 'default',
                                            }}
                                        />
                                    )}
                                </div>
                            </Tooltip>
                        )}
                    </IconsContainer>
                )}
            </Box>
        </RootContainer>
    )
}

const RootContainer = styled(Box)({
    display: 'flex',
    marginBottom: '8px',
    marginTop: '8px',
    alignItems: 'center',
    wordBreak: 'break-word',
})
const AvatarContainer = styled(Box)({
    marginRight: '10px',
    marginLeft: '5px',
    '& >div': {
        height: '30px',
        width: '30px',
    },
})
const IconsContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginTop: '2px',
    marginLeft: '-4px',
    '& >svg': {
        margin: '0px 4px',
        height: '14px',
        width: '14px',
    },
})
